import type { NearbyPlace, PlaceCategory } from '../hooks/useNearbyPlaces';

export interface NeighborhoodFactor {
  key: string;
  score: number;
}

export interface NeighborhoodSummary {
  hasData: boolean;
  overallScore: number;
  factors: NeighborhoodFactor[];
}

// The 5 displayed factors, each fed by one or more live Google place categories.
// (Dining/Recreation aren't part of the 5-factor model and are intentionally omitted.)
const FACTOR_CATEGORIES: Array<{ key: string; categories: PlaceCategory[] }> = [
  { key: 'schools', categories: ['EDUCATION'] },
  { key: 'services', categories: ['SHOPPING', 'FINANCE'] },
  { key: 'healthcare', categories: ['HEALTHCARE'] },
  { key: 'transport', categories: ['TRANSPORTATION'] },
  { key: 'religious', categories: ['WORSHIP'] },
];

const round1 = (n: number) => Math.round(n * 10) / 10;

/**
 * Derive a conservative neighbourhood summary from the LIVE nearby-places data
 * (Google Places, already fetched + cached by useNearbyPlaces). Real data, no
 * fabrication: a factor only appears if at least one matching place exists. The
 * score blends proximity (how close the nearest one is) with a small density
 * bonus, and is deliberately capped below a perfect 5.
 */
export function deriveNeighborhoodFromNearby(
  placesByCategory: Partial<Record<PlaceCategory, NearbyPlace[]>>,
): NeighborhoodSummary {
  const factors: NeighborhoodFactor[] = [];

  for (const f of FACTOR_CATEGORIES) {
    const places = f.categories.flatMap((c) => placesByCategory[c] ?? []);
    if (places.length === 0) continue;

    const nearestKm = Math.min(...places.map((p) => p.distanceKm));
    // Proximity baseline - conservative, never a perfect 5.
    const proximity =
      nearestKm <= 0.5 ? 4.6
      : nearestKm <= 1 ? 4.2
      : nearestKm <= 2 ? 3.8
      : nearestKm <= 3 ? 3.4
      : 3.0;
    // Density bonus: more nearby options nudge it up, capped at +0.3.
    const bonus = Math.min(places.length, 5) * 0.06;
    factors.push({ key: f.key, score: round1(Math.min(4.8, proximity + bonus)) });
  }

  const hasData = factors.length > 0;
  const overallScore = hasData
    ? round1(factors.reduce((s, f) => s + f.score, 0) / factors.length)
    : 0;

  return { hasData, overallScore, factors };
}
